import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axiosInstance from 'api/axios';
import { Level, LevelState } from 'types/level';
import { getTierNamesExcludingFirst } from 'utils/levelHelpers';

const initialState: LevelState = {
  levels: [],
  subLevels: {},
  isLoading: false,
  error: null,
  bonusMatrix: null,
  isBonusMatrixLoading: false,
  bonusMatrixError: null,
};

type FetchLevelsArg = string | number | { siteId?: string | number; silent?: boolean } | void;

export const fetchLevels = createAsyncThunk(
  'level/fetchLevels',
  async (arg: FetchLevelsArg, { rejectWithValue }) => {
    try {
      let siteId;
      if (typeof arg === 'object' && arg !== null) {
        siteId = arg.siteId;
      } else {
        siteId = arg;
      }

      const targetSiteId = siteId || process.env.REACT_APP_TENANT_ID || '1';
      const res = await axiosInstance.get(`/client/levels?siteId=${targetSiteId}`);
      if (res.status === 200) {
        return res?.data?.data?.levels ?? res?.data?.data ?? [];
      }
      return [];
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to load levels.');
    }
  },
);

type FetchBonusMatrixArg = string | number | { siteId?: string | number; silent?: boolean } | void;

export const fetchBonusMatrix = createAsyncThunk(
  'level/fetchBonusMatrix',
  async (arg: FetchBonusMatrixArg, { rejectWithValue }) => {
    try {
      let siteId;
      if (typeof arg === 'object' && arg !== null) {
        siteId = arg.siteId;
      } else {
        siteId = arg;
      }

      const targetSiteId = siteId || process.env.REACT_APP_TENANT_ID || '1';
      const res = await axiosInstance.get(`/client/levels/bonus-matrix?siteId=${targetSiteId}`);
      if (res.status === 200) {
        return res?.data?.data ?? null;
      }
      return null;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to load bonus matrix.');
    }
  },
);

const levelSlice = createSlice({
  name: 'level',
  initialState,
  reducers: {
    clearLevelError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLevels.pending, (state, action) => {
        const arg = action.meta.arg;
        const isSilent = typeof arg === 'object' && arg !== null && arg.silent;

        if (!isSilent) {
          state.isLoading = true;
        }
        state.error = null;
      })
      .addCase(fetchLevels.fulfilled, (state, action: PayloadAction<Level[]>) => {
        state.isLoading = false;
        state.levels = getTierNamesExcludingFirst(action.payload);
        state.subLevels = action.payload;
      })
      .addCase(fetchLevels.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchBonusMatrix.pending, (state, action) => {
        const arg = action.meta.arg;
        const isSilent = typeof arg === 'object' && arg !== null && arg.silent;
        if (!isSilent && !state.bonusMatrix) {
          state.isBonusMatrixLoading = true;
        }
        state.bonusMatrixError = null;
      })
      .addCase(fetchBonusMatrix.fulfilled, (state, action) => {
        state.isBonusMatrixLoading = false;
        state.bonusMatrix = action.payload;
      })
      .addCase(fetchBonusMatrix.rejected, (state, action) => {
        state.isBonusMatrixLoading = false;
        state.bonusMatrixError = action.payload as string;
      });
  },
});

export const { clearLevelError } = levelSlice.actions;
export default levelSlice.reducer;
